'use client';

import React, { useState, useEffect } from 'react';
import { 
  Search, MapPin, ExternalLink, Leaf, Trash2, ShoppingBag, 
  HeartHandshake, Wrench, Calculator, Calendar, List, Grid, 
  Network, Map as MapIcon, Info, Eye, Mail, CheckCircle, 
  Instagram, Globe, MessageCircle, PlusCircle, Repeat, PenTool, 
  Filter, Clock, X, ChevronRight, SlidersHorizontal, AlertCircle, 
  HelpCircle, Sun, Moon, Tent, Microscope, ShieldCheck, Zap, 
  Users, Target, Lock, ArrowRight, BookOpen, BarChart3, FileText
} from 'lucide-react';

/* * SECURITY NOTE (CVE-2025-55184 & CVE-2025-55183 Compliance):
 * This component is Client-Side Only. 
 * If connecting to a backend, ensure strict input validation to prevent RSC vulnerabilities.
 */

// --- TRANSLATIONS ---
const UI_TEXT = {
  en: {
    subtitle: "Make Sustainability Visible. From daily habits to deep tech solutions.",
    searchPlaceholder: "Search (e.g. Mushroom, Halal, Plastic)...",
    dispose: "DISPOSE",
    shop: "SHOP",
    join: "CONNECT",
    create: "CREATE",
    visit: "VISIT",
    research: "FUTURE", 
    events: "EVENTS", 
    tools: "TOOLS",
    home: "HOME",
    welcome: "The Ecosystem of Change",
    welcomeSub: "Kentara means 'Visible'. We map the fragmented sustainability landscape.",
    wasteReceivers: "Waste Receivers",
    sustainableGoods: "Sustainable Goods",
    movements: "Communities & Networks",
    createSolutions: "Workshops & Repair",
    visitDestinations: "Slow Travel & Culture",
    researchInnovation: "Research & Deep Tech", 
    upcomingEvents: "Upcoming Events",
    usefulTools: "Calculators & Policy",
    legend: "Legend",
    easy: "Easy Swap",
    impact: "Impact Upgrade",
    circular: "Circular Hero",
    earn: "Earn",
    pay: "Pay",
    free: "Free",
    knowSolution: "Know a solution we missed?",
    submitListing: "Submit a New Listing",
    namePlaceholder: "Name of Organization/Brand",
    linkPlaceholder: "Link / Contact (IG/Web)",
    detailsPlaceholder: "Additional details (optional)...",
    cancel: "Cancel",
    submit: "Submit Listing",
    thanks: "Thanks for joining!",
    joinMovement: "Join the Movement",
    emailPlaceholder: "you@example.com",
    subscribe: "Subscribe",
    welcomeMessage: "We've sent a welcome guide to your inbox.",
    newsletterDesc: "Get weekly eco-tips and local updates delivered to your inbox.",
    submittedMessage: "Success! Your listing has been submitted for review.",
    yourAction: "Your Action",
    whyGood: "Why it's important:",
    visitStore: "Visit Store",
    details: "Details",
    found: "found",
    filterOrigin: "Origin",
    filterCost: "Cost",
    filterAccess: "Access",
    filterCat: "Category",
    filterLoc: "Location",
    reset: "Reset Filters",
    all: "All",
    local: "Indonesia",
    global: "Global",
    disclaimerTitle: "Disclaimer & Data Policy",
    disclaimerText: "Information on Kentara.xyz is curated from public sources (Social Media, Websites) for educational purposes. We are not affiliated with these entities unless stated. Links may be referral links to support our platform.",
    takedownRequest: "Are you the owner? Request update or removal here.",
    sourceLabel: "Data Source:",
    sdgLabel: "Supported UN SDGs:", 
    certLabel: "Certifications & Standards:", 
    coverageLabel: "Service Coverage:", 
    viewAs: "View Mode:",
    about: {
      title: "About Kentara",
      whatTitle: "What is this?",
      whatText: "Kentara.xyz is a digital ecosystem map designed to centralize sustainability information in Indonesia. We aggregate scattered data—from waste banks and eco-brands to research labs and communities.",
      whyTitle: "Why Kentara?",
      whyText: "The word 'Kentara' means 'Visible' or 'Clear' in Indonesian. Sustainability solutions exist, but they are often invisible to the public. We bridge the gap between intent ('I want to help') and action ('Here is where I go').",
      howTitle: "How it works",
      howText: "We curate data from public sources and community submissions. We categorize them not just by industry, but by 'Action' (Dispose, Shop, Create) to fit into your daily lifestyle.",
      team: "Built by the community, for the community.",
      securityTitle: "Security & Transparency",
      securityText: "We are committed to transparent operations. Our platform runs on secure frameworks patched against known vulnerabilities (CVE-2025-55184). We prioritize data privacy."
    },
    legendTooltips: { 
      easy: "Low effort, < 5 mins, or saves money immediately. Best for beginners.",
      impact: "Requires lifestyle shift or physical presence. Significant positive impact.",
      circular: "Directly solves waste problem (Trash to Treasure) or extends product life."
    },
    moneyLabels: {
      paySub: "Pay Subscription",
      earnPoints: "Earn Points",
      earnCash: "Earn Cash",
      groceryPoints: "Grocery Points",
      payService: "Pay Service",
      eduFund: "Edu/Health Fund",
      donation: "Donation",
      sembako: "Sembako",
      commFund: "Community Fund",
      ticket: "Workshop Ticket",
      serviceFee: "Service Fee",
      freeAccess: "Free Access",
      eventTicket: "Event Ticket",
      entryFee: "Entry Fee",
      stayRate: "Nightly Rate",
      grant: "Grant/Funded",
      policy: "Regulation"
    },
    shopTypes: { 
      new: "Buy New",
      secondhand: "Secondhand",
      rent: "Rent",
      service: "Service"
    },
    stockTypes: { 
      ready: "Ready Stock",
      preorder: "Made to Order"
    },
    researchStatus: { 
      published: "Published",
      pilot: "Pilot Project",
      active: "Active Org",
      concept: "Concept/R&D"
    },
    detailLabels: { 
      wasteAccepted: "Waste Accepted",
      circularFeatures: "Circular Features",
      materials: "Materials",
      contactInfo: "Contact & Links",
      researchArea: "Research Focus",
      keyPoints: "Key Takeaways" 
    }
  },
  id: {
    subtitle: "Jadikan keberlanjutan terlihat. Dari kebiasaan kecil hingga teknologi masa depan.",
    searchPlaceholder: "Cari (mis. Jamur, Halal, Plastik)...",
    dispose: "SETOR", 
    shop: "BELANJA",
    join: "KONEKSI", 
    create: "KREASI",
    visit: "WISATA",
    research: "RISET", 
    events: "ACARA", 
    tools: "ALAT",
    home: "BERANDA",
    welcome: "Ekosistem Perubahan",
    welcomeSub: "Kentara berarti 'Terlihat'. Kami memetakan solusi hijau yang seringkali tersembunyi.",
    viewAs: "Mode Tampilan:",
    wasteReceivers: "Tempat Penyaluran Sampah",
    sustainableGoods: "Produk & Brand Hijau",
    movements: "Komunitas & Relawan",
    createSolutions: "Workshop & Reparasi",
    visitDestinations: "Ekowisata & Budaya",
    researchInnovation: "Riset & Inovasi", 
    upcomingEvents: "Agenda Kegiatan", 
    usefulTools: "Kalkulator & Data",
    legend: "Keterangan",
    easy: "Langkah Awal", 
    impact: "Berdampak",
    circular: "Sirkular",
    earn: "Dapat Poin/Uang",
    pay: "Berbayar",
    free: "Gratis",
    knowSolution: "Punya info solusi hijau lainnya?",
    submitListing: "Tambah Data Baru",
    namePlaceholder: "Nama Organisasi/Brand",
    linkPlaceholder: "Link / Kontak (IG/Web)",
    detailsPlaceholder: "Ceritakan detailnya (opsional)...",
    cancel: "Batal",
    submit: "Kirim Data",
    thanks: "Terima kasih!",
    joinMovement: "Gabung Komunitas",
    emailPlaceholder: "emailmu@contoh.com",
    subscribe: "Langganan",
    welcomeMessage: "Panduan pemula telah dikirim ke email Anda.",
    newsletterDesc: "Dapatkan tips praktis dan update lokal mingguan.",
    submittedMessage: "Sukses! Data Anda sedang kami tinjau.",
    yourAction: "Aksi Kamu",
    whyGood: "Kenapa Penting:",
    visitStore: "Lihat Toko",
    details: "Selengkapnya",
    found: "data ditemukan",
    filterOrigin: "Asal:",
    filterCost: "Biaya:",
    filterAccess: "Akses:",
    filterCat: "Kategori:",
    filterLoc: "Lokasi:",
    reset: "Hapus Filter",
    all: "Semua",
    local: "Indonesia",
    global: "Global",
    disclaimerTitle: "Kebijakan Data",
    disclaimerText: "Data di Kentara.xyz dikurasi dari sumber publik untuk edukasi. Kami tidak berafiliasi dengan entitas tersebut kecuali dinyatakan lain.",
    takedownRequest: "Pemilik bisnis? Ajukan update data di sini.",
    sourceLabel: "Sumber:",
    sdgLabel: "Mendukung SDGs:", 
    certLabel: "Sertifikasi:", 
    coverageLabel: "Area Layanan:",
    about: {
      title: "Tentang Kentara",
      whatTitle: "Apa ini?",
      whatText: "Kentara.xyz adalah peta digital untuk memusatkan informasi keberlanjutan di Indonesia. Kami mengumpulkan data yang tersebar—dari bank sampah, brand lokal, hingga riset energi.",
      whyTitle: "Kenapa Kentara?",
      whyText: "Solusi ramah lingkungan itu ada, tapi seringkali 'tidak terlihat'. Kami hadir untuk menjembatani niat baik ('Saya ingin peduli') dengan aksi nyata ('Ke sini saya harus pergi').",
      howTitle: "Cara Kerja",
      howText: "Kami mengkurasi data publik dan kiriman komunitas. Data dikategorikan berdasarkan 'Aksi' (Setor, Belanja, Kreasi) agar relevan dengan kebutuhan sehari-hari Anda.",
      team: "Dibangun oleh komunitas, untuk komunitas.",
      securityTitle: "Keamanan & Transparansi",
      securityText: "Platform ini berjalan pada sistem yang aman dan telah diperbarui (sesuai standar keamanan terbaru CVE-2025). Kami memprioritaskan privasi data pengguna."
    },
    legendTooltips: { 
      easy: "Mudah, < 5 menit, atau hemat biaya. Cocok untuk pemula.",
      impact: "Butuh perubahan kebiasaan. Dampak positif lebih besar.",
      circular: "Mengubah sampah jadi berkah atau memperpanjang umur barang."
    },
    moneyLabels: {
      paySub: "Langganan",
      earnPoints: "Dapat Poin",
      earnCash: "Dapat Uang",
      groceryPoints: "Tukar Sembako",
      payService: "Berbayar",
      eduFund: "Dana Pendidikan",
      donation: "Donasi",
      sembako: "Tukar Sembako",
      commFund: "Dana Komunitas",
      ticket: "Tiket",
      serviceFee: "Biaya Jasa",
      freeAccess: "Gratis",
      eventTicket: "Tiket Acara",
      entryFee: "Tiket Masuk",
      stayRate: "Tarif/Malam",
      grant: "Hibah",
      policy: "Regulasi"
    },
    shopTypes: { 
      new: "Beli Baru",
      secondhand: "Bekas",
      rent: "Sewa",
      service: "Jasa"
    },
    stockTypes: { 
      ready: "Ready",
      preorder: "Pre-Order"
    },
    researchStatus: { 
      published: "Terpublikasi",
      pilot: "Pilot Project",
      active: "Aktif",
      concept: "Konsep"
    },
    detailLabels: { 
      wasteAccepted: "Terima Sampah",
      circularFeatures: "Fitur Sirkular",
      materials: "Material",
      contactInfo: "Kontak",
      researchArea: "Fokus Riset",
      keyPoints: "Poin Penting" 
    }
  }
};

// --- SDG HELPER ---
const SDG_ICONS = {
  3: { label: "Good Health", color: "bg-green-600" },
  4: { label: "Quality Education", color: "bg-red-600" },
  6: { label: "Clean Water", color: "bg-blue-400" },
  7: { label: "Clean Energy", color: "bg-yellow-500" }, 
  8: { label: "Decent Work", color: "bg-red-700" },
  9: { label: "Innovation", color: "bg-orange-600" }, 
  11: { label: "Sustainable Cities", color: "bg-orange-500" },
  12: { label: "Consumption", color: "bg-yellow-600" },
  13: { label: "Climate Action", color: "bg-green-700" },
  14: { label: "Life Below Water", color: "bg-blue-500" },
  15: { label: "Life on Land", color: "bg-green-500" },
  17: { label: "Partnerships", color: "bg-blue-700" }
};

// --- FULL DATASETS ---

const DISPOSE_DATA = [
  { id: 19, name: "Sociolla Recycle", type: "Drop-off", location: "Nationwide Malls", coords: {x: 200, y: 230}, money: "+", moneyKey: "earnPoints", focus: "Beauty Packaging", score: "easy", isGlobal: false, details: ["Kosmetik Bekas", "Skincare Bottles", "SOCO Points"], source: "Following @sociolla", sdgs: [12], contacts: { web: "sociolla.com", ig: "sociolla" }, coverage: "Sociolla Stores in Malls" }, 
  { id: 20, name: "The Body Shop", type: "Drop-off", location: "Nationwide Malls", coords: {x: 205, y: 230}, money: "+", moneyKey: "earnPoints", focus: "Beauty Packaging", score: "easy", isGlobal: true, details: ["TBS Bottles", "Return & Recycle", "Points"], source: "Following @thebodyshopindo", sdgs: [12], contacts: { web: "thebodyshop.co.id", ig: "thebodyshopindo" }, coverage: "All TBS Stores" }, 
  { id: 21, name: "Parongpong RAW Lab", type: "Lab", location: "Bandung", coords: {x: 195, y: 240}, money: "-", moneyKey: "payService", focus: "Zero Waste Solutions", score: "circular", isGlobal: false, details: ["Cigarette Butts -> Bricks", "Event Waste Management", "Consultancy"], source: "Following @parong.pong", sdgs: [9, 11, 12], contacts: { ig: "parong.pong" } }, 
  { id: 16, name: "Duitin", type: "Tech App", location: "Jabodetabek, Java", coords: {x: 192, y: 226}, money: "+", moneyKey: "earnCash", focus: "Recyclables Pickup", score: "easy", isGlobal: false, details: ["Botol Plastik", "Karton/Kardus", "Kaca", "Minyak Jelantah"], source: "Following @duitin_id", sdgs: [11, 12], contacts: { ig: "duitin_id", web: "duitin.com" }, coverage: "Jabodetabek, Surabaya, Semarang, Denpasar" }, 
  { id: 17, name: "Golimbah", type: "Tech Service", location: "Jabodetabek", coords: {x: 190, y: 235}, money: "+", moneyKey: "earnCash", focus: "Waste Management", score: "circular", isGlobal: false, details: ["Guna Olah Limbah", "All Inorganic", "Pickup Service"], source: "Following @golimbah", sdgs: [12, 13], contacts: { ig: "golimbah" }, coverage: "Jakarta, Depok, Tangerang" }, 
  { id: 13, name: "Plasticpay", type: "Tech App", location: "Jabodetabek", coords: {x: 195, y: 228}, money: "+", moneyKey: "earnPoints", focus: "Plastic Bottles (RVM)", score: "easy", isGlobal: false, details: ["Botol Plastik PET", "Vending Machine", "Tukar Pulsa/E-money"], source: "Following @plasticpay_id", sdgs: [12, 14], contacts: { ig: "plasticpay_id", web: "plasticpay.id" }, coverage: "Jabodetabek (Mall/Station RVMs)" },
  { id: 14, name: "Kamibox", type: "Tech Service", location: "Jabodetabek", coords: {x: 182, y: 232}, money: "+", moneyKey: "earnCash", focus: "Cardboard & Paper", score: "easy", isGlobal: false, details: ["Kardus Bekas", "Kertas Arsip", "Penjemputan Gratis (S&K)"], source: "Following @kamibox.id", sdgs: [12, 15], contacts: { ig: "kamibox.id" }, coverage: "Jakarta, Tangerang, Bekasi" },
  { id: 15, name: "Sampah Spesifik", type: "Specialist", location: "Jakarta", coords: {x: 188, y: 235}, money: "-", moneyKey: "payService", focus: "Hazardous/Specific", score: "circular", isGlobal: false, details: ["Limbah B3", "Elektronik", "Obat Kadaluarsa", "Kosmetik Bekas"], source: "Following @sampahspesifik", sdgs: [3, 12], contacts: { ig: "sampahspesifik" }, coverage: "DKI Jakarta (Drop-off Points)" },
  { id: 1, name: "Waste4Change", type: "Tech Service", location: "Jabodetabek", coords: {x: 180, y: 230}, money: "-", moneyKey: "paySub", focus: "All Inorganic", score: "circular", isGlobal: false, details: ["Residu", "Sachet", "Styrofoam", "Kaca", "Elektronik"], source: "Official Website", sdgs: [11, 12, 13], contacts: { web: "waste4change.com", ig: "waste4change", wa: "6282111100170" }, coverage: "Jabodetabek, Bandung, Surabaya" },
  { id: 2, name: "Rekosistem", type: "Tech App", location: "Jakarta", coords: {x: 185, y: 225}, money: "+", moneyKey: "earnPoints", focus: "Plastic, Paper, Oil", score: "easy", isGlobal: false, details: ["Minyak Jelantah", "Kardus", "Botol Plastik", "Kaleng", "E-waste"], source: "App Store Description", sdgs: [12, 13], contacts: { web: "rekosistem.com", ig: "rekosistem" }, coverage: "Jakarta, Surabaya, Bandung, Malang" },
  { id: 3, name: "BS Induk Surabaya", type: "Central Hub", location: "Surabaya", coords: {x: 280, y: 250}, money: "+", moneyKey: "earnCash", focus: "Comprehensive", score: "impact", isGlobal: false, details: ["Koran", "Besi", "Tembaga", "Botol Kaca", "Plastik Keras"], source: "Public Directory", sdgs: [11, 12], contacts: { wa: "6285100090858", ig: "bsinduksurabaya" }, coverage: "Kota Surabaya" },
  { id: 4, name: "Rapel", type: "Tech App", location: "Yogyakarta", coords: {x: 230, y: 255}, money: "+", moneyKey: "earnCash", focus: "Plastic, Metal", score: "easy", isGlobal: false, details: ["Botol PET", "Kertas HVS", "Logam", "Elektronik Rusak"], source: "Official Website", sdgs: [12], contacts: { web: "rapel.id", ig: "rapel_id" }, coverage: "Yogyakarta City, Sleman" },
  { id: 6, name: "EcoBali Recycling", type: "Private", location: "Bali", coords: {x: 320, y: 260}, money: "-", moneyKey: "payService", focus: "Paper, Glass", score: "circular", isGlobal: false, details: ["Tetra Pak", "Botol Wine", "Kaca Pecah", "Kertas"], source: "Official Website", sdgs: [12, 14], contacts: { web: "eco-bali.com", ig: "ecobalirecycle" }, coverage: "Badung, Denpasar" },
  { id: 18, name: "Armada Kemasan", type: "Recycler", location: "Surabaya", coords: {x: 282, y: 252}, money: "0", moneyKey: "freeAccess", focus: "Multilayer", score: "circular", isGlobal: false, details: ["Kemasan Sachet", "Kemasan Refill", "Multilayer Plastic"], source: "Following @armadakemasan", sdgs: [12], contacts: { ig: "armadakemasan" }, coverage: "Surabaya, Sidoarjo" }
];

const SHOP_DATA = [
  { id: 31, name: "Pable.id", category: "Fashion", location: "Surabaya", coords: {x: 280, y: 250}, price: "Rp 100k+", money: "-", reason: "Textile Recycling", score: "circular", shopType: "new", stockType: "ready", isGlobal: false, details: ["Recycled Fabric", "Circular Fashion", "Textile Waste Mgmt"], source: "Following @pable.id", sdgs: [12], contacts: { web: "pable.id", ig: "pable.id" } }, 
  { id: 32, name: "EcoTouch", category: "Home", location: "Bandung", coords: {x: 195, y: 240}, price: "Rp 100k+", money: "-", reason: "Denim Insulation", score: "circular", shopType: "new", stockType: "preorder", isGlobal: false, details: ["Recycled Denim", "Thermal Insulation", "Acoustic Panel"], source: "Following @ecotouch_id", sdgs: [9, 12], contacts: { ig: "ecotouch_id" } }, 
  { id: 27, name: "MYCL (Mycotech)", category: "Fashion", location: "Bandung", coords: {x: 195, y: 240}, price: "Rp 500k+", money: "-", reason: "Mushroom Leather", score: "circular", shopType: "new", stockType: "preorder", isGlobal: false, details: ["Mylea Leather", "Biodegradable"], source: "Following @mycl.bio", sdgs: [9, 12, 13], certs: ["Organik"], contacts: { web: "mycl.bio", ig: "mycl.bio" } },
  { id: 28, name: "Plepah", category: "Home", location: "Jakarta/Jambi", coords: {x: 185, y: 230}, price: "Rp 5k/pc", money: "-", reason: "Palm Waste Packaging", score: "circular", shopType: "new", stockType: "ready", isGlobal: false, details: ["Food Container", "Areca Palm Sheath", "Compostable"], source: "Following @plepah_id", sdgs: [12, 15], certs: ["Halal"], contacts: { web: "plepah.com", ig: "plepah_id" } },
  { id: 29, name: "Torajamelo", category: "Fashion", location: "Toraja/Jkt", coords: {x: 350, y: 200}, price: "Rp 500k+", money: "-", reason: "Cultural Preservation", score: "impact", shopType: "new", stockType: "ready", isGlobal: false, details: ["Tenun Toraja", "Women Empowerment"], source: "Following @torajamelo", sdgs: [1, 5, 8], contacts: { web: "torajamelo.com", ig: "torajamelo" } }, 
  { id: 30, name: "Byo Living", category: "Home", location: "Jakarta", coords: {x: 188, y: 228}, price: "Custom", money: "-", reason: "Weaving Architecture", score: "circular", shopType: "service", stockType: "preorder", isGlobal: true, details: ["Sustainable Architecture", "Recycled Weaving"], source: "Following @byoliving", sdgs: [9, 11], contacts: { web: "byoliving.com", ig: "byoliving" } }, 
  { id: 19, name: "SukkhaCitta", category: "Fashion", location: "Online/Jkt", coords: {x: 188, y: 230}, price: "Rp 800k+", money: "-", reason: "Regenerative Farm-to-Closet", score: "circular", shopType: "new", stockType: "ready", isGlobal: false, isBCorp: true, details: ["B Corp Certified", "Pewarna Alam", "Regenerative Cotton"], source: "Certified B Corp", sdgs: [1, 12, 15], certs: ["B Corp", "Organik"], contacts: { web: "sukkhacitta.com", ig: "sukkhacitta" } },
  { id: 20, name: "Indosole", category: "Fashion", location: "Bali/Online", coords: {x: 320, y: 265}, price: "Rp 400k+", money: "-", reason: "Tire Sole Footwear", score: "circular", shopType: "new", stockType: "ready", isGlobal: true, isBCorp: true, details: ["B Corp Certified", "Recycled Tires", "Vegan"], source: "Certified B Corp", sdgs: [12], certs: ["B Corp"], contacts: { web: "indosole.com", ig: "indosole" } },
  { id: 23, name: "Yagi Forest", category: "Personal Care", location: "Aceh/Online", coords: {x: 100, y: 100}, price: "Rp 90k+", money: "-", reason: "Sustainable Skincare", score: "impact", shopType: "new", stockType: "ready", isGlobal: false, details: ["Cocoa Butter", "Natural Ingredients", "Fair Trade"], source: "Following @yagi.forest", sdgs: [12, 15], certs: ["Halal", "BPOM"], contacts: { ig: "yagi.forest", web: "yaginatural.com" } }, 
  { id: 24, name: "Robries", category: "Home", location: "Surabaya", coords: {x: 285, y: 255}, price: "Rp 150k+", money: "-", reason: "Recycled Plastic Furniture", score: "circular", shopType: "new", stockType: "preorder", isGlobal: false, details: ["Tables", "Chairs", "100% Recycled Plastic"], source: "Following @robries", sdgs: [12], contacts: { ig: "robries", web: "robries.com" } }, 
  { id: 25, name: "Kertas Kertasan", category: "Create", location: "Bandung", coords: {x: 200, y: 240}, price: "Rp 50k+", money: "-", reason: "Recycled Paper Art", score: "circular", shopType: "new", stockType: "ready", isGlobal: false, details: ["Daur Ulang Kertas", "Handmade Paper"], source: "Following @kertas.kertasan", sdgs: [12], contacts: { ig: "kertas.kertasan" } }, 
  { id: 26, name: "Sustaination", category: "Home", location: "Online", coords: {x: 220, y: 220}, price: "Rp 35k+", money: "-", reason: "Zero Waste Basics", score: "easy", shopType: "new", stockType: "ready", isGlobal: false, details: ["Menstrual Cup", "Composting Kit"], source: "Following @sustaination", sdgs: [12], contacts: { web: "sustaination.id", ig: "sustaination" } }, 
  { id: 21, name: "Napak Bhumi", category: "Fashion", location: "Yogyakarta", coords: {x: 232, y: 252}, price: "Rp 350k+", money: "-", reason: "Upcycled Tire Shoes", score: "circular", shopType: "new", stockType: "preorder", isGlobal: false, details: ["Sol Ban Bekas", "Sisa Kulit/Kain", "Handmade"], source: "User Request", sdgs: [12], contacts: { ig: "napak.bhumi" } },
  { id: 13, name: "Tinctori", category: "Fashion", location: "Jakarta", coords: {x: 188, y: 228}, price: "Rp 300k+", money: "-", reason: "Natural Dyes", score: "impact", shopType: "new", stockType: "preorder", isGlobal: false, details: ["Pewarna Alam", "Bahan Organik"], source: "Following @tinctori", sdgs: [12], contacts: { ig: "tinctori" } }, 
  { id: 14, name: "Plustik", category: "Home", location: "Jakarta", coords: {x: 190, y: 232}, price: "Rp 50k+", money: "-", reason: "Recycled Plastic Goods", score: "circular", shopType: "new", stockType: "ready", isGlobal: false, details: ["Paving Block", "Furniture", "Recycled PP/HDPE"], source: "Following @plustik.id", sdgs: [11, 12], contacts: { ig: "plustik.id" } }, 
  { id: 15, name: "Reencle Indonesia", category: "Home", location: "Nationwide", coords: {x: 250, y: 150}, price: "Rp 10jt+", money: "-", reason: "Home Composter", score: "impact", shopType: "new", stockType: "ready", isGlobal: false, details: ["Komposter Elektrik", "Zero Food Waste"], source: "Following @reencle_indonesia", sdgs: [12], contacts: { ig: "reencle_indonesia" } }, 
  { id: 16, name: "Demibumi.id", category: "Home", location: "Jakarta", coords: {x: 185, y: 235}, price: "Rp 25k+", money: "-", reason: "Zero Waste Kit", score: "easy", shopType: "new", stockType: "ready", isGlobal: false, details: ["Beeswax Wrap", "Lerak", "Loofah", "Net Bag"], source: "Following @demibumi.id", sdgs: [12, 14], contacts: { web: "demibumi.id", ig: "demibumi.id" } }, 
  { id: 1, name: "Sayurbox (Imperfect)", category: "Food", location: "Jakarta", coords: {x: 180, y: 230}, price: "Rp 10k", money: "-", reason: "Rescues veg", score: "easy", shopType: "new", stockType: "ready", isGlobal: false, details: ["Rescued Food", "Plastic-free options"], source: "App Catalog", sdgs: [12], contacts: { web: "sayurbox.com", ig: "sayurbox" } },
  { id: 6, name: "Pasar Senen Thrift", category: "Fashion", location: "Jakarta", coords: {x: 188, y: 225}, price: "Rp 35k", money: "-", reason: "Textile rescue", score: "circular", shopType: "secondhand", stockType: "ready", isGlobal: false, details: ["Zero New Resources", "Extends Garment Life"], source: "Public Info", sdgs: [12], contacts: { google: "Pasar Senen Blok III" } },
  { id: 12, name: "Style Theory", category: "Service", location: "Jakarta", coords: {x: 185, y: 225}, price: "Rp 590k/mo", money: "-", reason: "Rental over buying", score: "impact", shopType: "rent", stockType: "ready", isGlobal: true, details: ["Access over Ownership", "Professional Cleaning"], contacts: { web: "styletheory.co", ig: "styletheoryid" } },
];

const JOIN_DATA = [
  { id: 20, name: "CarbonEthics", category: "Nature", location: "Online/Coastal", coords: {x: 300, y: 200}, action: "Donate", reason: "Blue Carbon", score: "impact", isGlobal: false, details: ["Mangrove Planting", "Carbon Offset", "Coastal Community"], source: "Following @carbonethics", sdgs: [13, 14], contacts: { web: "carbonethics.org", ig: "carbonethics" } }, 
  { id: 18, name: "Pandawara Group", category: "Cleanup", location: "Bandung/Nationwide", coords: {x: 210, y: 240}, action: "Volunteer", reason: "River Cleanup", score: "circular", isGlobal: false, details: ["Pembersihan Sungai", "Viral Action", "Youth Movement"], source: "Following @pandawaragroup", sdgs: [6, 14], contacts: { ig: "pandawaragroup" } }, 
  { id: 21, name: "Bio Rock Indonesia", category: "Nature", location: "Bali", coords: {x: 330, y: 260}, action: "Donate", reason: "Coral Restoration", score: "impact", isGlobal: false, details: ["Terumbu Karang", "Konservasi Laut", "Edukasi"], source: "Following @biorockindonesia", sdgs: [14], contacts: { ig: "biorockindonesia" } },
  { id: 22, name: "Teens Go Green", category: "Youth", location: "Jakarta", coords: {x: 185, y: 230}, action: "Join", reason: "Youth Education", score: "easy", isGlobal: false, details: ["Edukasi Lingkungan", "Anak Muda", "Workshop"], source: "Following @teensgogreen.id", sdgs: [4, 13], contacts: { ig: "teensgogreen.id" } },
  { id: 23, name: "Scholars of Sustenance", category: "Food", location: "Bali/Jakarta", coords: {x: 320, y: 270}, action: "Volunteer", reason: "Food Rescue", score: "circular", isGlobal: true, details: ["Food Rescue", "Zero Hunger", "Food Bank"], source: "Following @scholarsofsustenanceid", sdgs: [2, 12], contacts: { ig: "scholarsofsustenanceid" } },
  { id: 11, name: "Greeners.co", category: "Media", location: "Online", coords: {x: 200, y: 40}, action: "Read", reason: "News & Info", score: "easy", isGlobal: false, details: ["Berita Lingkungan", "Edukasi", "Event Info"], source: "Following @greeners.co", sdgs: [13], contacts: { ig: "greeners.co", web: "greeners.co" } }, 
  { id: 12, name: "Cleanomic", category: "Media", location: "Online", coords: {x: 220, y: 40}, action: "Read", reason: "Eco-Economics", score: "easy", isGlobal: false, details: ["Tips Hemat", "Gaya Hidup Minim Sampah", "Ekonomi Sirkular"], source: "Following @cleanomic", sdgs: [12], contacts: { ig: "cleanomic" } }, 
  { id: 13, name: "ADUPI", category: "Advocacy", location: "Nationwide", coords: {x: 240, y: 220}, action: "Collaborate", reason: "Plastic Recycling", score: "impact", isGlobal: false, details: ["Asosiasi Daur Ulang", "Koneksi Bisnis", "Edukasi Industri"], source: "Following @adupi_indonesia", sdgs: [12, 17], contacts: { ig: "adupi_indonesia" } }, 
  { id: 14, name: "Peduli Anak", category: "Social", location: "Lombok", coords: {x: 340, y: 260}, action: "Donate", reason: "Child Welfare", score: "impact", isGlobal: false, details: ["Yayasan Anak", "Pendidikan", "Kesehatan"], source: "Following @pedulianak", sdgs: [3, 4], contacts: { ig: "pedulianak", web: "pedulianak.org" } },
  { id: 15, name: "Sebumi.id", category: "Lifestyle", location: "Online", coords: {x: 210, y: 50}, action: "Learn", reason: "Eco-Education", score: "easy", isGlobal: false, details: ["Webinar", "Workshop", "Konservasi"], source: "Following @sebumi.id", sdgs: [4, 13], contacts: { ig: "sebumi.id" } }, 
  { id: 16, name: "Lyfe With Less", category: "Lifestyle", location: "Online", coords: {x: 215, y: 50}, action: "Practice", reason: "Minimalism", score: "easy", isGlobal: false, details: ["Minimalisme", "Decluttering", "Mindful Living"], source: "Following @lyfewithless", sdgs: [12], contacts: { ig: "lyfewithless" } }, 
  { id: 17, name: "Hutan Itu Indonesia", category: "Nature", location: "Online", coords: {x: 250, y: 50}, action: "Campaign", reason: "Forest Protection", score: "easy", isGlobal: false, details: ["Adopsi Hutan", "Kampanye Digital", "Music for Nature"], source: "Following @hutanituid", sdgs: [15], contacts: { ig: "hutanituid" } }, 
  { id: 1, name: "Sungai Watch", category: "Cleanup", location: "Bali", coords: {x: 325, y: 265}, action: "Volunteer", reason: "Ocean Protection", score: "impact", isGlobal: false, contacts: { web: "sungai.watch", ig: "sungaiwatch" } },
  { id: 2, name: "Trash Hero ID", category: "Cleanup", location: "Nationwide", coords: {x: 250, y: 200}, action: "Join Sunday", reason: "City Cleanup", score: "impact", isGlobal: true, contacts: { ig: "trashheroindonesia" } },
  { id: 3, name: "Garda Pangan", category: "Food", location: "Surabaya", coords: {x: 280, y: 250}, action: "Volunteer", reason: "Food Rescue", score: "circular", isGlobal: false, contacts: { ig: "gardapangan" } },
  { id: 4, name: "LindungiHutan", category: "Nature", location: "Online", coords: {x: 250, y: 50}, action: "Donate Tree", reason: "Reforestation", score: "easy", isGlobal: false, contacts: { web: "lindungihutan.com" } },
  { id: 28, name: "Kunto Aji", category: "Voices", location: "Online", coords: {x: 220, y: 50}, action: "Follow", reason: "Mental Health/Eco", score: "easy", isGlobal: false, details: ["Musician", "Mindfulness", "Earth Hour Supporter"], source: "Public Figure", sdgs: [3], contacts: { ig: "kuntoajiw" } }, 
];

const CREATE_DATA = [
  { id: 11, name: "Risocycle", type: "Studio", location: "Jakarta", coords: {x: 188, y: 232}, money: "-", moneyKey: "serviceFee", focus: "Riso Printing", score: "circular", isGlobal: false, details: ["Risograph Printing", "Ramah Lingkungan", "Soy-based Ink"], source: "Following @risocycle", sdgs: [12], contacts: { ig: "risocycle" } }, 
  { id: 12, name: "Fashion Crafty Jkt", type: "Workshop", location: "Jakarta", coords: {x: 182, y: 238}, money: "-", moneyKey: "ticket", focus: "Craft Workshops", score: "easy", isGlobal: false, details: ["Menjahit", "Crafting", "Kelas Tatap Muka"], source: "Following @fashioncraftyjakarta", sdgs: [4, 12], contacts: { ig: "fashioncraftyjakarta" } }, 
  { id: 13, name: "Upcycle Denim Project", type: "Workshop", location: "Jakarta", coords: {x: 190, y: 230}, money: "-", moneyKey: "ticket", focus: "Denim Upcycling", score: "circular", isGlobal: false, details: ["Upcycle Denim", "Sashiko", "Workshop"], source: "Following @upcycledenimprojex", sdgs: [12], contacts: { ig: "upcycledenimprojex" } },
  { id: 1, name: "Kebun Kumara", type: "Workshop", location: "Jabodetabek", coords: {x: 180, y: 235}, money: "-", moneyKey: "ticket", focus: "Urban Farming", score: "impact", isGlobal: false, contacts: { ig: "kebunkumara" } },
  { id: 2, name: "Setali Upcycling", type: "Service", location: "Jakarta", coords: {x: 185, y: 230}, money: "-", moneyKey: "serviceFee", focus: "Remake Old Clothes", score: "circular", isGlobal: false, details: ["Upcycling Baju Bekas", "Custom Remake", "Workshop Jahit"], source: "Following @setali.indonesia", sdgs: [12], contacts: { ig: "setali.indonesia" } },
  { id: 3, name: "Threadapeutic", type: "Workshop", location: "Jakarta", coords: {x: 182, y: 228}, money: "-", moneyKey: "ticket", focus: "Fabric Upcycling", score: "circular", isGlobal: false, contacts: { web: "threadapeutic.com" } },
  { id: 4, name: "Kertabumi Center", type: "Workshop", location: "Tangerang", coords: {x: 175, y: 232}, money: "-", moneyKey: "ticket", focus: "Plastic Recycling", score: "impact", isGlobal: false, details: ["Daur Ulang Plastik", "Workshop", "Edukasi"], source: "Following @kertabumirecyclingcenter", sdgs: [12], contacts: { ig: "kertabumirecyclingcenter" } },
];

const TOOLS_DATA = [
  { id: 11, name: "Youth Sustainability Index", type: "Report", location: "Indonesia", coords: {x: 210, y: 50}, money: "0", moneyKey: "freeAccess", focus: "Gen Z Research", score: "impact", isGlobal: false, details: ["Insight Gen Z", "Environmental Psychology", "Download PDF"], source: "WWF Indonesia", sdgs: [4, 13], contacts: { web: "plasticsmartcities.wwf.id" } }, 
  { id: 1, name: "Jejak.in", type: "Calculator", location: "Indonesia", coords: {x: 200, y: 50}, money: "0", moneyKey: "freeAccess", focus: "Carbon Calculator", score: "impact", isGlobal: false, contacts: { web: "jejak.in" } },
  { id: 2, name: "WWF Footprint", type: "Calculator", location: "Global", coords: {x: 220, y: 50}, money: "0", moneyKey: "freeAccess", focus: "Ecological Footprint", score: "easy", isGlobal: true, contacts: { web: "footprint.wwf.org.uk" } },
  { id: 3, name: "Zero Waste Home", type: "Guide", location: "Global", coords: {x: 240, y: 50}, money: "0", moneyKey: "freeAccess", focus: "Waste Audit Guide", score: "easy", isGlobal: true, contacts: { web: "zerowastehome.com" } },
  { id: 5, name: "PLN Mobile", type: "Utility", location: "Indonesia", coords: {x: 190, y: 60}, money: "0", moneyKey: "freeAccess", focus: "Energy Usage", score: "easy", isGlobal: false, contacts: { app: "PLN Mobile" } },
];

const EVENTS_DATA = [
  { id: 1, name: "Festival Jajanan Bango", type: "Festival", location: "Jakarta", date: "Sept 2025", money: "+", moneyKey: "eventTicket", focus: "Kuliner Lokal", score: "easy", isGlobal: false, contacts: { ig: "bangowarisankuliner" } },
  { id: 2, name: "IdeaFest 2025", type: "Conference", location: "JCC Senayan", date: "Oct 2025", money: "-", moneyKey: "eventTicket", focus: "Creative Sustain", score: "impact", isGlobal: false, contacts: { web: "ideafest.id" } },
  { id: 3, name: "Pasar Wiguna", type: "Eco Market", location: "Bandung", date: "Weekly Sun", money: "0", moneyKey: "freeAccess", focus: "Eco Tenants", score: "easy", isGlobal: false, contacts: { ig: "pasarwiguna" } },
];

const VISIT_DATA = [
  { id: 8, name: "Bumi Langit", type: "Institute", location: "Yogyakarta", coords: {x: 240, y: 250}, money: "-", moneyKey: "ticket", focus: "Permaculture & Adat", score: "circular", isGlobal: false, details: ["Permaculture Farm", "Warung Bumi", "Educational Tour"], source: "Following @bumilangit.institute", sdgs: [4, 12, 15], certs: ["Halal"], contacts: { ig: "bumilangit.institute", web: "bumilangit.org" } }, 
  { id: 1, name: "Desa Potato Head", type: "Hotel", location: "Bali", coords: {x: 320, y: 270}, money: "-", moneyKey: "stayRate", focus: "Zero Waste Hospitality", score: "circular", isGlobal: false, isBCorp: true, details: ["Zero Waste Lab", "Regenerative Design", "Cultural Hub"], source: "Certified B Corp", sdgs: [11, 12], contacts: { web: "seminyak.potatohead.co", ig: "potatoheadbali" } },
  { id: 2, name: "Mana Earthly Paradise", type: "Hotel", location: "Ubud, Bali", coords: {x: 322, y: 260}, money: "-", moneyKey: "stayRate", focus: "Eco Stay", score: "circular", isGlobal: false, isBCorp: true, details: ["B Corp Certified", "Probiotic Food", "Earthbag Villas"], source: "Certified B Corp", sdgs: [12, 13], contacts: { web: "manaubud.com", ig: "manaubud" } },
  { id: 4, name: "Rumah Atsiri", type: "Museum", location: "Tawangmangu", coords: {x: 250, y: 250}, money: "-", moneyKey: "entryFee", focus: "Aromatic Garden", score: "easy", isGlobal: false, details: ["Essential Oil Museum", "Garden Tour", "Workshops"], source: "Following @atsirijakarta", sdgs: [4, 15], contacts: { web: "rumahatsiri.com", ig: "rumahatsiri" } },
  { id: 5, name: "Museum Nasional", type: "Museum", location: "Jakarta", coords: {x: 185, y: 230}, money: "-", moneyKey: "entryFee", focus: "Cultural Heritage", score: "easy", isGlobal: false, details: ["Sejarah Indonesia", "Budaya", "Edukasi"], source: "Following @museumnasionalindonesia", sdgs: [11], contacts: { web: "museumnasional.or.id", ig: "museumnasionalindonesia" } },
  { id: 6, name: "Indonesia Hidden Heritage", type: "Tour", location: "Nationwide", coords: {x: 250, y: 200}, money: "-", moneyKey: "serviceFee", focus: "Heritage Tours", score: "impact", isGlobal: false, details: ["Walking Tour", "Sejarah", "Konservasi"], source: "Following @indonesiahiddenheritage", sdgs: [11], contacts: { ig: "indonesiahiddenheritage" } },
];

const RESEARCH_DATA = [
  { id: 1, name: "BRIN", type: "Institution", location: "Indonesia", coords: {x: 220, y: 60}, money: "", moneyKey: "grant", focus: "National Research", score: "circular", isGlobal: false, status: "active", details: ["Badan Riset Inovasi Nasional", "Energy", "Biotech"], source: "Public Institution", sdgs: [9, 17], contacts: { web: "brin.go.id" } },
  { id: 2, name: "ITB Energy Policy", type: "Lab", location: "Bandung", coords: {x: 195, y: 242}, money: "", moneyKey: "freeAccess", focus: "Energy Transition", score: "impact", isGlobal: false, status: "active", details: ["Renewable Policy", "Academic Papers"], source: "University", sdgs: [7, 13], contacts: { web: "itb.ac.id" } },
  { id: 3, name: "Youth Sustainability Index", type: "Report", location: "Indonesia", coords: {x: 210, y: 50}, money: "", moneyKey: "freeAccess", focus: "Gen Z Research", score: "impact", isGlobal: false, status: "published", details: ["Insight Gen Z", "Environmental Psychology", "Download PDF"], source: "WWF Indonesia", sdgs: [4, 13], contacts: { web: "plasticsmartcities.wwf.id" } },
  { id: 4, name: "SRE Indonesia", type: "Organization", location: "Nationwide", coords: {x: 230, y: 200}, money: "", moneyKey: "freeAccess", focus: "Renewable Energy", score: "impact", isGlobal: false, status: "active", details: ["Student Movement", "Solar Projects", "Clean Energy Advocacy"], source: "Community", sdgs: [7, 13], contacts: { ig: "sreindonesia" } },
  { id: 5, name: "IESR", type: "Think Tank", location: "Jakarta", coords: {x: 185, y: 230}, money: "", moneyKey: "freeAccess", focus: "Energy Transition", score: "impact", isGlobal: false, status: "active", details: ["Essential Services Reform", "Solar Policy", "Decarbonization"], source: "Research", sdgs: [7, 13], contacts: { web: "iesr.or.id" } },
  { id: 6, name: "ThorCon Power", type: "Company", location: "Indonesia", coords: {x: 260, y: 180}, money: "", moneyKey: "grant", focus: "Nuclear Energy", score: "circular", isGlobal: false, status: "pilot", details: ["Molten Salt Reactor", "Thorium", "Clean Energy Pilot"], source: "Industry News", sdgs: [7, 9], contacts: { web: "thorconpower.com" } },
];

// --- COMPONENTS ---

const HoverCard = ({ children, content }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div className="relative inline-block" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      {children}
      {isHovered && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-slate-800 text-white text-xs rounded-lg p-3 shadow-xl pointer-events-none animate-fade-in z-[100]">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-8 border-transparent border-t-slate-800"></div>
        </div>
      )}
    </div>
  );
};

// --- ABOUT MODAL (SECURE & IMPROVED) ---
const AboutModal = ({ onClose, t, isDark }) => (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
    <div className={`${isDark ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-800'} rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl relative`} onClick={e => e.stopPropagation()}>
      
      {/* Header */}
      <div className={`p-6 pb-4 border-b ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
        <div className="flex justify-between items-start">
          <div>
            <span className="text-xs font-bold text-emerald-500 uppercase tracking-wider mb-1 block">Kentara.xyz</span>
            <h2 className="text-2xl font-bold">{t.about.title}</h2>
          </div>
          <button onClick={onClose} className={`p-2 rounded-full hover:bg-opacity-20 transition-colors ${isDark ? 'hover:bg-white text-slate-400' : 'hover:bg-black text-slate-500'}`}>
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        
        {/* Transparency/Security Notice */}
        <div className={`p-3 rounded-xl border flex items-start gap-3 ${isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-100'}`}>
           <ShieldCheck size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
           <div>
             <h4 className={`text-xs font-bold mb-1 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>{t.about.securityTitle}</h4>
             <p className={`text-[10px] leading-relaxed ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>
               {t.about.securityText}
             </p>
           </div>
        </div>

        {/* Why */}
        <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-emerald-50/50 border-emerald-100'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Eye className="text-emerald-500" size={20} />
            <h3 className="font-bold text-lg">{t.about.whyTitle}</h3>
          </div>
          <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            {t.about.whyText}
          </p>
        </div>

        {/* What */}
        <div className="flex gap-4">
          <div className="flex-1">
             <div className="flex items-center gap-2 mb-2">
              <Target className="text-blue-500" size={18} />
              <h3 className="font-bold text-sm">{t.about.whatTitle}</h3>
            </div>
            <p className={`text-xs leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {t.about.whatText}
            </p>
          </div>
          <div className="flex-1">
             <div className="flex items-center gap-2 mb-2">
              <Zap className="text-yellow-500" size={18} />
              <h3 className="font-bold text-sm">{t.about.howTitle}</h3>
            </div>
            <p className={`text-xs leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {t.about.howText}
            </p>
          </div>
        </div>

        {/* Values Icons */}
        <div className="grid grid-cols-3 gap-2 text-center">
            <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100'}`}>
              <Users size={20} className="mx-auto mb-2 text-purple-500" />
              <span className="text-[10px] font-bold block">Community</span>
            </div>
            <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100'}`}>
              <Leaf size={20} className="mx-auto mb-2 text-green-500" />
              <span className="text-[10px] font-bold block">Impact</span>
            </div>
            <div className={`p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100'}`}>
              <Lock size={20} className="mx-auto mb-2 text-blue-500" />
              <span className="text-[10px] font-bold block">Privacy First</span>
            </div>
        </div>

        <div className={`text-center pt-4 border-t ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
           <p className="text-xs italic text-slate-400">{t.about.team}</p>
        </div>
      </div>
    </div>
  </div>
);

// --- DETAIL MODAL ---
const DetailModal = ({ item, onClose, t, isDark }) => {
  if (!item) return null;
  
  let detailLabel = t.detailLabels.circularFeatures;
  if (item.moneyKey === "earnPoints" || item.moneyKey === "earnCash") detailLabel = t.detailLabels.wasteAccepted;
  if (item.type === "Calculator") detailLabel = "Features";
  if (item.type === "Hotel" || item.type === "Stay") detailLabel = "Amenities";
  if (item.category === "Research" || item.type === "Institution") detailLabel = t.detailLabels.researchArea;
  if (item.type === "Policy" || item.type === "Report") detailLabel = t.detailLabels.keyPoints;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${isDark ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-800'} rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto shadow-2xl animate-scale-in`} onClick={e => e.stopPropagation()}>
        <div className="p-6 relative">
          <button onClick={onClose} className={`absolute top-4 right-4 p-2 rounded-full hover:bg-opacity-20 transition-colors ${isDark ? 'hover:bg-white' : 'hover:bg-black'}`}>
            <X size={20} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
          </button>
          
          <div className="mb-6">
            <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider bg-emerald-50 px-2 py-1 rounded mb-2 inline-block">
              {item.type || item.category}
            </span>
            <h2 className="text-2xl font-bold mb-1 flex items-center gap-2">
              {item.name}
              {item.isBCorp && (
                 <span title="Certified B Corp" className="bg-black text-white text-[10px] font-bold px-1.5 py-0.5 rounded border border-slate-200 cursor-help">
                   B Corp
                 </span>
              )}
            </h2>
            <div className={`flex items-center text-sm gap-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <MapPin size={14} /> {item.location}
            </div>
          </div>

          <div className="space-y-6">
            {/* Details List */}
            {item.details && item.details.length > 0 && (
              <div className={`p-4 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100'}`}>
                <h4 className={`font-bold text-sm mb-3 flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  <Info size={16} className="text-emerald-500" /> {detailLabel}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {item.details.map((detail, idx) => (
                    <span key={idx} className={`text-xs border px-3 py-1.5 rounded-full font-medium shadow-sm ${isDark ? 'bg-slate-900 border-slate-700 text-slate-300' : 'bg-white border-slate-200 text-slate-600'}`}>
                      {detail}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Coverage */}
            {item.coverage && (
               <div className={`p-4 rounded-xl border ${isDark ? 'bg-blue-900/30 border-blue-800' : 'bg-blue-50 border-blue-100'}`}>
                  <h4 className={`font-bold text-sm mb-1 flex items-center gap-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                    <MapIcon size={14} /> {t.coverageLabel}
                  </h4>
                  <p className={`text-xs ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>{item.coverage}</p>
               </div>
            )}

            {/* Certifications */}
            {item.certs && (
              <div className={`p-4 rounded-xl border ${isDark ? 'bg-purple-900/20 border-purple-800' : 'bg-purple-50 border-purple-100'}`}>
                 <h4 className={`font-bold text-sm mb-2 flex items-center gap-2 ${isDark ? 'text-purple-300' : 'text-purple-800'}`}>
                   <ShieldCheck size={14} /> {t.certLabel}
                 </h4>
                 <div className="flex flex-wrap gap-2">
                   {item.certs.map((cert, idx) => (
                     <span key={idx} className={`text-xs font-bold px-2 py-1 rounded border ${isDark ? 'bg-purple-900 text-purple-200 border-purple-700' : 'bg-white text-purple-700 border-purple-200'}`}>
                       {cert}
                     </span>
                   ))}
                 </div>
              </div>
            )}

            {/* SDGs */}
            {item.sdgs && (
              <div className="space-y-2">
                <h4 className={`font-bold text-xs uppercase ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t.sdgLabel}</h4>
                <div className="flex flex-wrap gap-2">
                  {item.sdgs.map(sdg => (
                    <span key={sdg} className={`text-[10px] font-bold text-white px-2 py-1 rounded ${SDG_ICONS[sdg]?.color || 'bg-slate-400'}`}>
                      SDG {sdg}: {SDG_ICONS[sdg]?.label || ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Source */}
            {item.source && (
              <div className={`text-xs italic flex items-center gap-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                <Eye size={10} /> {t.sourceLabel} {item.source}
              </div>
            )}

            {/* Status & Score */}
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-3 rounded-xl border ${isDark ? 'bg-green-900/30 border-green-800' : 'bg-green-50 border-green-100'}`}>
                <div className="text-[10px] font-bold text-green-600 uppercase mb-1">Benefit/Cost</div>
                <div className={`font-bold text-sm ${isDark ? 'text-green-400' : 'text-green-800'}`}>
                   {item.status && t.researchStatus && t.researchStatus[item.status] 
                     ? <span className={`px-2 py-1 rounded text-xs uppercase ${isDark ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>{t.researchStatus[item.status]}</span>
                     : (t.moneyLabels && t.moneyLabels[item.moneyKey]) || item.price || t.free}
                </div>
              </div>
              <div className={`p-3 rounded-xl border ${isDark ? 'bg-yellow-900/30 border-yellow-800' : 'bg-yellow-50 border-yellow-100'}`}>
                <div className="text-[10px] font-bold text-yellow-600 uppercase mb-1">Impact Level</div>
                <div className={`font-bold text-sm capitalize ${isDark ? 'text-yellow-400' : 'text-yellow-800'}`}>{item.score}</div>
              </div>
            </div>

            {/* Contacts */}
            <div>
              <h4 className={`font-bold text-sm mb-3 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{t.detailLabels.contactInfo}</h4>
              <div className="flex flex-col gap-2">
                {item.contacts?.web && (
                  <a href={`https://${item.contacts.web}`} target="_blank" rel="noreferrer" className={`flex items-center justify-between p-3 rounded-lg border transition-all group ${isDark ? 'border-slate-700 hover:bg-slate-800' : 'border-slate-200 hover:bg-slate-50 hover:border-emerald-300'}`}>
                    <span className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}><Globe size={16} /> Website</span>
                    <ExternalLink size={16} className="text-slate-400 group-hover:text-emerald-500" />
                  </a>
                )}
                {item.contacts?.ig && (
                  <a href={`https://instagram.com/${item.contacts.ig}`} target="_blank" rel="noreferrer" className={`flex items-center justify-between p-3 rounded-lg border transition-all group ${isDark ? 'border-slate-700 hover:bg-slate-800' : 'border-slate-200 hover:bg-pink-50 hover:border-pink-300'}`}>
                    <span className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}><Instagram size={16} /> Instagram</span>
                    <span className="text-xs text-pink-500 font-bold">@{item.contacts.ig}</span>
                  </a>
                )}
                {item.contacts?.wa && (
                  <a href={`https://wa.me/${item.contacts.wa}`} target="_blank" rel="noreferrer" className={`flex items-center justify-between p-3 rounded-lg border transition-all group ${isDark ? 'border-slate-700 hover:bg-slate-800' : 'border-slate-200 hover:bg-green-50 hover:border-green-300'}`}>
                    <span className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}><MessageCircle size={16} /> WhatsApp</span>
                    <span className="text-xs text-green-500 font-bold">Chat Now</span>
                  </a>
                )}
              </div>
            </div>
            
            <div className={`text-center pt-4 border-t ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
              <a href="mailto:info@kentara.xyz" className="text-[10px] text-slate-400 hover:text-red-400 transition-colors underline">
                {t.takedownRequest}
              </a>
            </div>
          </div>

          <button onClick={onClose} className={`w-full mt-8 py-3 rounded-xl font-bold text-sm transition-colors ${isDark ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-slate-900 text-white hover:bg-black'}`}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// --- HOME DASHBOARD VIEW ---
const HomeView = ({ setTab, t, isDark, onOpenAbout }) => (
  <div className="pt-2">
    {/* Mission Card */}
    <div className={`p-6 rounded-2xl border mb-6 relative overflow-hidden ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 rounded-full blur-xl transform translate-x-5 -translate-y-5"></div>
        <h3 className={`font-bold text-lg mb-2 ${isDark ? 'text-emerald-400' : 'text-emerald-800'}`}>
          {t.welcome}
        </h3>
        <p className={`text-sm mb-4 leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
          {t.welcomeSub}
        </p>
        <button onClick={onOpenAbout} className="text-xs font-bold text-emerald-600 hover:underline flex items-center gap-1">
          Read our story <ChevronRight size={12}/>
        </button>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div onClick={() => setTab('dispose')} className={`p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all cursor-pointer flex flex-col items-center text-center group border ${isDark ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-white border-slate-100'}`}>
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center text-red-600 mb-3 group-hover:scale-110 transition-transform"><Trash2 size={32} /></div>
        <h3 className={`font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{t.dispose}</h3>
        <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-400'}`}>Recycle & Waste</p>
      </div>
      <div onClick={() => setTab('shop')} className={`p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all cursor-pointer flex flex-col items-center text-center group border ${isDark ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-white border-slate-100'}`}>
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 mb-3 group-hover:scale-110 transition-transform"><ShoppingBag size={32} /></div>
        <h3 className={`font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{t.shop}</h3>
        <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-400'}`}>Eco-Goods</p>
      </div>
      <div onClick={() => setTab('create')} className={`p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all cursor-pointer flex flex-col items-center text-center group border ${isDark ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-white border-slate-100'}`}>
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mb-3 group-hover:scale-110 transition-transform"><Wrench size={32} /></div>
        <h3 className={`font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{t.create}</h3>
        <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-400'}`}>Repair & Upcycle</p>
      </div>
      <div onClick={() => setTab('visit')} className={`p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all cursor-pointer flex flex-col items-center text-center group border ${isDark ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-white border-slate-100'}`}>
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 mb-3 group-hover:scale-110 transition-transform"><Tent size={32} /></div>
        <h3 className={`font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{t.visit}</h3>
        <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-400'}`}>Slow Travel</p>
      </div>
      <div onClick={() => setTab('research')} className={`p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all cursor-pointer flex flex-col items-center text-center group border ${isDark ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-white border-slate-100'}`}>
        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 mb-3 group-hover:scale-110 transition-transform"><Microscope size={32} /></div>
        <h3 className={`font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{t.research}</h3>
        <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-400'}`}>Deep Tech</p>
      </div>
      <div onClick={() => setTab('join')} className={`p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all cursor-pointer flex flex-col items-center text-center group border ${isDark ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-white border-slate-100'}`}>
        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 mb-3 group-hover:scale-110 transition-transform"><HeartHandshake size={32} /></div>
        <h3 className={`font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{t.join}</h3>
        <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-400'}`}>Community</p>
      </div>
    </div>
  </div>
);

// --- MAIN APP COMPONENT ---
export default function App() {
  const [activeTab, setActiveTab] = useState('home'); 
  const [viewMode, setViewMode] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [lang, setLang] = useState('en');
  const [originFilter, setOriginFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [costFilter, setCostFilter] = useState('all');
  const [accessFilter, setAccessFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [locFilter, setLocFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewCount, setViewCount] = useState(1240);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitData, setSubmitData] = useState({ name: '', type: 'dispose', link: '', details: '' }); 
  const [submitted, setSubmitted] = useState(false);
  const [isDark, setIsDark] = useState(false); 
  const [showAbout, setShowAbout] = useState(false);

  // Data State
  const [disposeData, setDisposeData] = useState(DISPOSE_DATA);
  const [shopData, setShopData] = useState(SHOP_DATA);
  const [joinData, setJoinData] = useState(JOIN_DATA);
  const [createData, setCreateData] = useState(CREATE_DATA);
  const [toolsData, setToolsData] = useState(TOOLS_DATA);
  const [eventsData, setEventsData] = useState(EVENTS_DATA);
  const [visitData, setVisitData] = useState(VISIT_DATA);
  const [researchData, setResearchData] = useState(RESEARCH_DATA);

  const t = UI_TEXT[lang]; 

  useEffect(() => {
    const interval = setInterval(() => {
        const randomIncrement = Math.floor(Math.random() * 3) + 1; 
        setViewCount(prev => prev + randomIncrement);
    }, 5000); 
    return () => clearInterval(interval);
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if(email) setSubscribed(true);
  };

  const handleSubmitSolution = (e) => {
    e.preventDefault();
    // Security: Only client-side update for prototype. 
    // In production, send to API with validation.
    const newItem = {
      id: Date.now(),
      name: submitData.name,
      type: "User Suggested",
      location: "Indonesia", 
      coords: {x: 250, y: 250}, 
      money: "?", 
      moneyKey: "freeAccess",
      focus: "User Submission",
      score: "easy",
      isGlobal: false,
      source: "User Submission",
      details: [submitData.details],
      contacts: { web: submitData.link }
    };

    if (submitData.type === 'dispose') setDisposeData(prev => [newItem, ...prev]);
    if (submitData.type === 'shop') setShopData(prev => [newItem, ...prev]);
    if (submitData.type === 'join') setJoinData(prev => [newItem, ...prev]);
    if (submitData.type === 'create') setCreateData(prev => [newItem, ...prev]);
    if (submitData.type === 'visit') setVisitData(prev => [newItem, ...prev]);
    if (submitData.type === 'research') setResearchData(prev => [newItem, ...prev]);
    if (submitData.type === 'tools') setToolsData(prev => [newItem, ...prev]);

    setSubmitted(true);
    setTimeout(() => {
      setShowSubmit(false);
      setSubmitted(false);
      setSubmitData({ name: '', type: 'dispose', link: '', details: '' }); 
    }, 2000);
  };

  const resetFilters = () => {
    setOriginFilter('all');
    setCostFilter('all');
    setAccessFilter('all');
    setCatFilter('all');
    setLocFilter('all');
    setSearchQuery('');
  };

  const getActiveData = () => {
    if (activeTab === 'dispose') return disposeData;
    if (activeTab === 'shop') return shopData;
    if (activeTab === 'join') return joinData;
    if (activeTab === 'create') return createData;
    if (activeTab === 'events') return eventsData;
    if (activeTab === 'visit') return visitData;
    if (activeTab === 'research') return researchData;
    return toolsData;
  };

  const getLocations = () => ['all', 'Jakarta', 'Jabodetabek', 'Bandung', 'Surabaya', 'Bali', 'Yogyakarta', 'Online'];
  const getCategories = () => ['all', ...Array.from(new Set(getActiveData().map(item => item.category || item.type)))];

  const filterData = (data) => {
    return data.filter(item => {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        item.name.toLowerCase().includes(query) ||
        (item.location && item.location.toLowerCase().includes(query)) ||
        (item.focus && item.focus.toLowerCase().includes(query));
      let matchesOrigin = originFilter === 'all' ? true : originFilter === 'local' ? !item.isGlobal : item.isGlobal;
      let matchesCost = costFilter === 'all' ? true : costFilter === 'free' ? item.money === '0' : costFilter === 'pay' ? item.money === '-' : item.money === '+';
      let matchesLoc = locFilter === 'all' ? true : item.location.toLowerCase().includes(locFilter.toLowerCase());
      return matchesSearch && matchesOrigin && matchesCost && matchesLoc;
    });
  };

  const activeData = filterData(getActiveData());

  const getScoreColor = (score) => {
    switch(score) {
      case 'easy': return 'text-green-600 border-green-500';
      case 'impact': return 'text-yellow-600 border-yellow-500';
      case 'circular': return 'text-blue-600 border-blue-500';
      default: return 'text-gray-600 border-gray-300';
    }
  };

  // --- SUB-VIEWS ---
  const ListView = () => (
    <div className="space-y-4">
      {activeData.map(item => (
        <div key={item.id} onClick={() => setSelectedItem(item)} className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-100'} p-4 rounded-xl shadow-sm hover:shadow-md transition-all border-l-4 cursor-pointer active:scale-95 ${getScoreColor(item.score).split(' ')[1]}`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{item.type || item.category}</span>
                {item.isGlobal ? <span>🌎</span> : <span>🇮🇩</span>}
              </div>
              <h3 className={`font-bold text-lg flex items-center gap-1 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                {item.name} {item.isBCorp && <span className="text-[8px] border px-1 rounded">B</span>} <ChevronRight size={14} className="text-slate-300" />
              </h3>
            </div>
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${item.money === '+' ? 'bg-green-100 text-green-800' : item.money === '-' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
              {t.moneyLabels?.[item.moneyKey] || item.price || t.free}
            </span>
          </div>
          <div className={`mt-2 text-sm flex flex-col gap-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            <div className="flex items-center"><MapPin size={12} className="mr-2"/> {item.location}</div>
            <div className="flex items-center"><Info size={12} className="mr-2"/> {item.focus || item.reason}</div>
          </div>
        </div>
      ))}
    </div>
  );

  const TableView = () => (
    <div className={`${isDark ? 'bg-slate-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className={`w-full text-sm text-left ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          <thead className={`text-xs uppercase border-b ${isDark ? 'bg-slate-900 text-slate-400 border-slate-700' : 'text-slate-700 bg-slate-50'}`}>
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Loc</th><th className="px-4 py-3">Focus</th></tr>
          </thead>
          <tbody>
            {activeData.map((item, idx) => (
              <tr key={item.id} onClick={() => setSelectedItem(item)} className={`cursor-pointer ${idx % 2 === 0 ? (isDark ? 'bg-slate-800' : 'bg-white') : (isDark ? 'bg-slate-800/50' : 'bg-slate-50')}`}>
                <td className={`px-4 py-3 font-medium ${isDark ? 'text-slate-200' : 'text-slate-900'}`}>{item.name}</td>
                <td className="px-4 py-3">{item.location}</td>
                <td className="px-4 py-3">{item.focus || item.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const MapView = () => (
    <div className={`rounded-xl shadow-sm p-4 h-[400px] relative overflow-hidden border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-sky-100 border-sky-200'}`}>
      <p className="absolute top-2 left-2 text-xs font-bold px-2 py-1 rounded bg-white/50 text-sky-700">Indonesia Map (Simulated)</p>
      {activeData.map((item) => {
        const x = item.location.includes("Online") ? Math.random() * 300 : item.coords?.x || 150;
        const y = item.location.includes("Online") ? 50 : item.coords?.y || 150;
        return (
          <div key={item.id} className="absolute w-3 h-3 rounded-full bg-emerald-500 border border-white shadow-md hover:scale-150 transition-transform cursor-pointer" style={{ left: x, top: y }} onClick={() => setSelectedItem(item)}></div>
        );
      })}
    </div>
  );

  return (
    <div className={`min-h-screen font-sans pb-24 relative transition-colors duration-300 ${isDark ? 'bg-slate-950 text-slate-100' : 'bg-stone-50 text-slate-800'}`}>
      
      {selectedItem && <DetailModal item={selectedItem} onClose={() => setSelectedItem(null)} t={t} isDark={isDark} />}
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} t={t} isDark={isDark} />}
      
      {/* HEADER */}
      <div className="bg-emerald-900 text-white p-6 pb-8 rounded-b-[2.5rem] shadow-xl relative overflow-hidden">
        <div className="absolute top-4 right-6 flex items-center gap-2">
           <button onClick={() => setIsDark(!isDark)} className="p-1.5 rounded-full bg-emerald-800/50 text-emerald-100 hover:bg-emerald-700 transition-colors">
            {isDark ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <button onClick={() => setShowAbout(true)} className="p-1.5 rounded-full bg-emerald-800/50 text-emerald-100 hover:bg-emerald-700 transition-colors" title="About Kentara">
            <HelpCircle size={14} />
          </button>
          <button onClick={() => setLang(lang === 'en' ? 'id' : 'en')} className="flex items-center gap-1 bg-emerald-800/50 px-3 py-1 rounded-full text-[10px] font-bold text-emerald-100 hover:bg-emerald-700 transition-colors min-w-[50px] justify-center">
            {lang === 'en' ? <><Globe size={10} /> EN</> : <span className="text-sm leading-none">🇮🇩</span>}
          </button>
        </div>

        <div className="max-w-md mx-auto relative z-10">
          <h1 className="text-3xl font-bold tracking-tight mb-2 cursor-pointer" onClick={() => setActiveTab('home')}>Kentara.xyz</h1>
          <p className="text-emerald-100 text-sm mb-6 leading-relaxed">{t.subtitle}</p>
          
          <div className="relative flex gap-2">
            <div className="relative flex-grow">
              <input type="text" placeholder={t.searchPlaceholder} className={`w-full p-3 pl-10 rounded-xl shadow-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all ${isDark ? 'bg-slate-800 text-slate-100 placeholder-slate-400' : 'bg-white text-slate-800'}`} value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); if (activeTab === 'home' && e.target.value) setActiveTab('shop'); }} />
              <Search className={`absolute left-3 top-3.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} size={18} />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`p-3 rounded-xl shadow-lg transition-all ${showFilters ? 'bg-yellow-400 text-emerald-900' : isDark ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-500'}`}><SlidersHorizontal size={18} /></button>
          </div>
        </div>
      </div>

      {/* FILTERS */}
      {showFilters && (
        <div className="max-w-md mx-auto px-4 -mt-4 mb-6 relative z-20 animate-fade-in-down">
          <div className={`p-4 rounded-xl shadow-xl border ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100'}`}>
            <div className="flex justify-between items-center mb-3">
              <h3 className={`font-bold text-sm flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}><Filter size={14} /> Filters</h3>
              <button onClick={resetFilters} className="text-[10px] text-red-500 font-bold hover:underline">{t.reset}</button>
            </div>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-1">
                {getLocations().map(loc => (
                   <button key={loc} onClick={() => setLocFilter(loc)} className={`px-2 py-1 rounded text-[10px] font-bold border ${locFilter === loc ? 'bg-emerald-100 text-emerald-700' : isDark ? 'border-slate-700' : 'border-slate-200'}`}>{loc === 'all' ? t.all : loc}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NAVIGATION */}
      <div className={`max-w-md mx-auto px-4 mb-6 grid grid-cols-4 md:grid-cols-7 gap-2 relative z-10 ${showFilters ? '' : '-mt-6'}`}>
        {['dispose', 'shop', 'join', 'create', 'visit', 'research', 'tools'].map(tabKey => (
           <button key={tabKey} onClick={() => setActiveTab(tabKey)} className={`py-3 rounded-xl shadow-md text-[9px] font-bold flex flex-col items-center gap-1 transition-all ${activeTab === tabKey ? 'bg-white text-emerald-800 ring-2 ring-emerald-500 translate-y-1' : 'bg-emerald-800 text-emerald-100'}`}>
             {tabKey === 'dispose' && <Trash2 size={14} />}
             {tabKey === 'shop' && <ShoppingBag size={14} />}
             {tabKey === 'join' && <HeartHandshake size={14} />}
             {tabKey === 'create' && <Wrench size={14} />}
             {tabKey === 'visit' && <Tent size={14} />}
             {tabKey === 'research' && <Microscope size={14} />}
             {tabKey === 'tools' && <Calculator size={14} />}
             {t[tabKey]}
           </button>
        ))}
      </div>

      {/* CONTENT */}
      <div className="max-w-md mx-auto px-4 pb-4">
        {activeTab === 'home' ? (
           <HomeView setTab={setActiveTab} t={t} isDark={isDark} onOpenAbout={() => setShowAbout(true)} />
        ) : (
          <>
            <div className="flex justify-between items-center mb-2 px-1">
              <h2 className={`font-bold text-lg ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                 {activeTab === 'dispose' ? t.wasteReceivers : activeTab === 'shop' ? t.sustainableGoods : activeTab === 'join' ? t.movements : activeTab === 'create' ? t.createSolutions : activeTab === 'visit' ? t.visitDestinations : activeTab === 'research' ? t.researchInnovation : t.usefulTools}
              </h2>
              <span className="text-xs text-slate-500">{activeData.length} {t.found}</span>
            </div>
            
            <div className={`p-1 rounded-lg shadow-sm flex justify-between items-center border mb-4 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
              <span className="text-xs font-bold text-slate-400 ml-3 uppercase tracking-wider">{t.viewAs}</span>
              <div className="flex gap-1">
                <button onClick={() => setViewMode('list')} className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-emerald-100 text-emerald-700' : 'text-slate-400'}`}><List size={18} /></button>
                <button onClick={() => setViewMode('table')} className={`p-2 rounded-md ${viewMode === 'table' ? 'bg-emerald-100 text-emerald-700' : 'text-slate-400'}`}><Grid size={18} /></button>
                <button onClick={() => setViewMode('map')} className={`p-2 rounded-md ${viewMode === 'map' ? 'bg-emerald-100 text-emerald-700' : 'text-slate-400'}`}><MapIcon size={18} /></button>
              </div>
            </div>

            {viewMode === 'list' && <ListView />}
            {viewMode === 'table' && <TableView />}
            {viewMode === 'map' && <MapView />}
          </>
        )}
      </div>

      {/* SUBMIT FORM */}
      <div className="max-w-md mx-auto px-4 my-6">
        <div className={`border-2 border-dashed rounded-xl p-4 text-center ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-slate-100 border-slate-300'}`}>
          {!showSubmit ? (
            <button onClick={() => setShowSubmit(true)} className="flex items-center justify-center gap-2 w-full text-slate-500 font-bold hover:text-emerald-600 transition-colors">
              <PlusCircle size={20} /> {t.knowSolution}
            </button>
          ) : submitted ? (
            <div className="flex flex-col items-center text-green-600 py-4 animate-bounce">
              <CheckCircle size={32} className="mb-2" /> <span className="font-bold">{t.submittedMessage}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmitSolution} className="text-left space-y-3 animate-fade-in">
              <h4 className={`font-bold text-sm text-center mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{t.submitListing}</h4>
              <input type="text" placeholder={t.namePlaceholder} className={`w-full p-2 text-sm rounded border ${isDark ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-300'}`} required value={submitData.name} onChange={e => setSubmitData({...submitData, name: e.target.value})} />
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowSubmit(false)} className="flex-1 py-2 text-xs font-bold rounded text-slate-500 bg-slate-200">{t.cancel}</button>
                <button type="submit" className="flex-1 py-2 text-xs font-bold bg-emerald-600 text-white rounded">{t.submit}</button>
              </div>
            </form>
          )}
        </div>
      </div>
      
      {/* FOOTER */}
      <div className={`max-w-md mx-auto px-6 text-center border-t pt-6 pb-10 mt-10 ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
         <p className="text-[10px] text-slate-500">{t.disclaimerText}</p>
      </div>
    </div>
  );
}
